import { useCallback, useState } from 'react';
import { Card, CardContent, CircularProgress, Icon, Typography } from '@material-ui/core';
import { Formik } from 'formik';
import { motion } from 'framer-motion';

// local imports
import { CampaignService } from 'app/services';
import { useStateCallback } from 'app/hooks';
import { useCampaignsPharmaciesService, useQuery } from 'app/hooks/services';
import { EnvDefaults } from 'app/utils';
import AddPharmacyModal from './add-campaign-pharmacy';
import { CustomDataTable } from 'app/components/organisms/table';
import { FilterCampaignPharmacies } from 'app/components/organisms/filter-forms';
import { ModalForm } from 'app/components/molecules';
import { AddButton, CustomCard } from 'app/components/atoms';

const initialState = {
  is_all_turp_pharmacies: false,
  pharmacy_ids: []
};

const EditCampaignPharmacy = ({ campaignId, pharmacyIds, setPharmacyIds }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useStateCallback(false);

  const tableQueries = {
    limit: 25,
    skip: 0,
    campaign_id: campaignId,
    city_id: '',
    district_id: '',
    pharmacy_name: ''
  };

  // re-fetch func.
  const onSuccess = async () => await handleRefresh();

  const handleDeletePharmacy = async (campaignId, pharmacyId) => {
    const serviceFn = () => CampaignService.deleteCampaignPharmacy(campaignId, pharmacyId);
    try {
      await serviceFn();
      // re-fetch
      onSuccess();
    } catch (err) {
      console.log('Error -> ', err);
    }
  };

  // autocomplete filter input related funcs.
  const handleAutocomplete = (e, name) => {
    if (e) {
      setQueries(prevState => ({
        ...prevState,
        skip: 0,
        [name]: e.value
      }));
    } else {
      setQueries(prevState => ({
        ...prevState,
        skip: 0,
        [name]: ''
      }));
    }
  };

  // service connection hooks
  const { queries, isFilteringRef, handleFilterChange, handlePageChange, handleSort, setQueries } =
    useQuery(tableQueries);
  const { service, handleRefresh } = useCampaignsPharmaciesService(
    queries,
    isFilteringRef,
    handleDeletePharmacy
  );

  // open turp pharmacies modal list
  const handleAddNewRecord = () => {
    setOpen(true);
    setPharmacyIds(initialState);
  };

  const addButton = (
    <AddButton label="Eczane Ekle" onAddNewRecord={handleAddNewRecord} icon="store" />
  );

  // submit change password (initial login pwd change) form
  const handleAddPharmacy = useCallback(async () => {
    setLoading(true, async () => {
      try {
        await CampaignService.addCampaignToPharmacies(campaignId, pharmacyIds);
        setOpen(false);
        onSuccess();
      } catch (err) {
        console.log('Error -> ', err);
      } finally {
        setLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLoading, campaignId, pharmacyIds]);

  // variables - statesü
  const { is_all_turp_pharmacies, pharmacy_ids } = pharmacyIds;
  const disableSubmitBtn = is_all_turp_pharmacies === false && pharmacy_ids.length < 1;

  return (
    <>
      <motion.div initial={{ scale: 0, opacity: 0.2 }} animate={{ scale: 1, opacity: 1 }}>
        {/* Filter form */}
        <Card className="mb-16" elevation={4}>
          <CardContent>
            <div className="flex items-center">
              <Icon>filter_list</Icon>
              <Typography variant="subtitle1" className="text-gray-700 font-semibold mx-8">
                Filtrele
              </Typography>
              {isFilteringRef.current && (
                <CircularProgress color="secondary" size={22} thickness={6} />
              )}
            </div>

            <Formik initialValues={EnvDefaults.filterCampaignPharmaciesDefaults}>
              <FilterCampaignPharmacies
                handleChange={handleFilterChange}
                handleFilterAutoComplete={handleAutocomplete}
              />
            </Formik>
          </CardContent>
        </Card>

        <CustomCard icon="bxs-store" title="Seçilen Eczaneler" contentClassName="px-8  min-h-512">
          <CustomDataTable
            defaultSortId={queries?.orderBy}
            defaultDirection={queries?.sortBy}
            service={service}
            queries={queries}
            handlePageChange={handlePageChange}
            handleChange={handleFilterChange}
            handleSort={handleSort}
            addButton={addButton}
            handleRefreshTable={handleRefresh}
          />
        </CustomCard>

        {/* List of pharmacies not participate in the campaign -  Pharmacies Table */}
        <ModalForm
          open={open}
          onClose={() => setOpen(false)}
          title="Eklenecek Eczaneler"
          size="md"
          onSubmit={handleAddPharmacy}
          loading={loading}
          confirmBtnDisabled={disableSubmitBtn}
          btnConfirmText="Eczane Ekle">
          <AddPharmacyModal
            modal
            campaignId={campaignId}
            setPharmacyIds={setPharmacyIds}
            pharmacyIds={pharmacyIds}
          />
        </ModalForm>
      </motion.div>
    </>
  );
};

export default EditCampaignPharmacy;
